import { CompliancePageListDueDiligenceComplianceRecordsQuery, DueDiligenceRecordStatus } from '@/__generated__/graphql';
import ActionDropdown from '@/components/basicComponents/actionDropdown';
import Tag from '@/components/basicComponents/tag';
import { Loader } from '@/components/loaders/loader';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import {
  TableBodyWrapper,
  TableFixedCell,
  TableRowCell,
  TableScrollableCellsWrapper,
  TableWithHorizontalScroll,
  TableWithHorizontalScrollRowWrapper,
  TableWithHorizontalScrollWrapper,
  TextCell
} from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { ISort, TableColumns } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { getUTCDate } from '@/shared/utils/date';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface DueDiligenceHistoryTableProps {
  tableColumns: TableColumns[];
  tableRows: CompliancePageListDueDiligenceComplianceRecordsQuery['listDueDiligenceComplianceRecords']['records'];
  sort: ISort;
  loading: boolean;
  setSort: (value: ISort) => void;
  openPage: (path: string) => void;
}

export const DueDiligenceHistoryTable = ({ tableColumns, tableRows, sort, loading, setSort, openPage }: DueDiligenceHistoryTableProps) => {
  const { t } = useTranslation('enums');
  const { isTablet } = useResponsive();

  // 5 is the gap (spacing) between table cells
  const totalTableWidth = tableColumns.reduce((acc, column) => acc + column.width + 5, 0);

  return (
    <TableWithHorizontalScrollWrapper isTablet={isTablet}>
      <TableWithHorizontalScroll totalWidth={`${totalTableWidth}px`}>
        <TableHeader refetch={() => {}} columns={tableColumns} savedSort={sort} savedSetSort={setSort} withHorizontalScroll />
        <TableBodyWrapper>
          {loading ? (
            <Loader />
          ) : (
            <>
              {!tableRows.length ? (
                <NoResults />
              ) : (
                <>
                  {tableRows.map((row) => (
                    <TableWithHorizontalScrollRowWrapper key={row.id}>
                      <TableFixedCell id="fixedCell" width={tableColumns[0].width} alignItems="center">
                        <RedirectTitleCell onClick={() => openPage(`/strategyDetails/${row?.investment?.id}?backToTitle=Due Diligence History`)}>
                          {row?.investment?.name ?? ''}
                        </RedirectTitleCell>
                      </TableFixedCell>
                      <TableScrollableCellsWrapper alignItems="center">
                        <TableRowCell width={tableColumns[1].width} withHorizontalScroll>
                          <TextCell>
                            {row?.investment?.subAssetClass
                              ? `${row?.investment?.assetClass?.name} - ${row?.investment?.subAssetClass?.name}`
                              : row?.investment?.assetClass?.name}
                          </TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[2].width} withHorizontalScroll>
                          <TextCell>
                            {row?.status && (
                              <Tag styleType={row?.status === DueDiligenceRecordStatus.Approved ? 'valid' : 'warning'} size="md">
                                {t(`enums:DueDiligenceRecordStatus.${row?.status}`) ?? ''}
                              </Tag>
                            )}
                          </TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[3].width} withHorizontalScroll>
                          <TextCell>{`${row?.activeTier?.funnel?.name} - ${row?.activeTier?.name}`}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[4].width} withHorizontalScroll>
                          <TextCell>{`${row?.approver?.firstName} ${row?.approver?.lastName}`}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[5].width} withHorizontalScroll>
                          <TextCell>{row?.statusUpdatedAt && getUTCDate(new Date(row.statusUpdatedAt))}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[6].width} withHorizontalScroll>
                          <ActionDropdown
                            options={[
                              {
                                name: 'View Due Diligence',
                                onClick: () => openPage(`/compliance/dueDiligence/${row.id}?backToTitle=Due Diligence History`)
                              }
                            ]}
                          />
                        </TableRowCell>
                      </TableScrollableCellsWrapper>
                    </TableWithHorizontalScrollRowWrapper>
                  ))}
                </>
              )}
            </>
          )}
        </TableBodyWrapper>
      </TableWithHorizontalScroll>
    </TableWithHorizontalScrollWrapper>
  );
};

const StatusTagCell = styled.div<{ color: string; bgColor: string }>`
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
  padding: 6px 20px;
  border-radius: 5px;
  color: ${({ color }) => color};
  background-color: ${({ bgColor }) => bgColor};
`;
